import {
  Badge,
  Button,
  ButtonGroup,
  type ButtonProps,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  type TableProps,
  TableRow,
  Text,
  useToast,
} from "@prismicio/editor-ui"
import {
  type ElementRef,
  forwardRef,
  type PropsWithChildren,
  type ReactNode,
  useMemo,
} from "react"

import { Flag } from "../../app/components/locales/Flag"
import { sortRepositoryLocales } from "./localeUtils"
import { SetAsMasterDialog } from "./SetAsMasterDialog"
import {
  useDeleteRepositoryLocaleMutation,
  useRepositoryLocalesQuery,
  useSetRepositoryLocaleAsMasterMutation,
} from "./useRepositoryLocales"

export function LocalesTable() {
  const { data: repositoryLocales } = useRepositoryLocalesQuery()

  const { showToast } = useToast()
  const deleteRepositoryLocaleMutation = useDeleteRepositoryLocaleMutation({
    onSuccess: (repositoryLocale) => {
      showToast({ title: `Locale "${repositoryLocale.label}" deleted` })
    },
    onError: (repositoryLocale) => {
      showToast({
        title: `Unable to delete locale "${repositoryLocale.id}"`,
        icon: "alert",
      })
    },
  })
  const setRepositoryLocaleAsMasterMutation =
    useSetRepositoryLocaleAsMasterMutation({
      onSuccess: (repositoryLocale) => {
        showToast({ title: `Master locale "${repositoryLocale.label}" set` })
      },
      onError: (repositoryLocale) => {
        showToast({
          title: `Unable to set master locale "${repositoryLocale.id}"`,
          icon: "alert",
        })
      },
    })

  const isLoading =
    deleteRepositoryLocaleMutation.isPending ||
    setRepositoryLocaleAsMasterMutation.isPending

  const sortedRepositoryLocales = useMemo(
    () => sortRepositoryLocales(repositoryLocales),
    [repositoryLocales],
  )

  return (
    <LocalesTableTemplate isLoading={isLoading}>
      {sortedRepositoryLocales.map((repositoryLocale) => {
        const buttonProps = { disabled: isLoading || repositoryLocale.isMaster }
        return (
          <LocalesTableTemplateRow
            key={repositoryLocale.id}
            name={
              <>
                <Flag locale={repositoryLocale.id} />
                <Text
                  noWrap
                  title={repositoryLocale.label}
                  variant="bold"
                  sx={{ marginLeft: 16 }}
                >
                  {repositoryLocale.label}
                </Text>
                {repositoryLocale.isMaster && (
                  <Badge
                    color="green"
                    size="medium"
                    title="Master locale"
                    sx={{ marginLeft: 8 }}
                  />
                )}
              </>
            }
            localeCode={
              <Text color="grey11" noWrap variant="small">
                {repositoryLocale.id}
              </Text>
            }
            setAsMasterButton={
              <SetAsMasterDialog
                repositoryLocale={repositoryLocale}
                setRepositoryLocaleAsMasterMutation={
                  setRepositoryLocaleAsMasterMutation
                }
                trigger={
                  <LocalesTableTemplateSetAsMasterButton
                    {...buttonProps}
                    loading={
                      setRepositoryLocaleAsMasterMutation.isPending &&
                      setRepositoryLocaleAsMasterMutation.variables.id ===
                        repositoryLocale.id
                    }
                  />
                }
              />
            }
            deleteButton={
              <LocalesTableTemplateDeleteButton
                {...buttonProps}
                loading={
                  deleteRepositoryLocaleMutation.isPending &&
                  deleteRepositoryLocaleMutation.variables.id ===
                    repositoryLocale.id
                }
                onClick={() => {
                  deleteRepositoryLocaleMutation.mutate(repositoryLocale)
                }}
              />
            }
          />
        )
      })}
    </LocalesTableTemplate>
  )
}

type LocalesTableTemplateProps = PropsWithChildren<{
  isLoading: TableProps["isLoading"]
}>

function LocalesTableTemplate(props: LocalesTableTemplateProps) {
  const { children, ...otherProps } = props
  return (
    <Table {...otherProps} columnLayout="3fr 1fr 141px" isInteractive={false}>
      <TableHead>
        <TableRow>
          <LocalesTableHeadCell>Name</LocalesTableHeadCell>
          <LocalesTableHeadCell>Locale code</LocalesTableHeadCell>
          <LocalesTableHeadCell />
        </TableRow>
      </TableHead>
      <TableBody>{children}</TableBody>
    </Table>
  )
}

function LocalesTableHeadCell(props: PropsWithChildren) {
  const { children } = props
  return (
    <TableCell>
      <Text color="grey11" noSelect noWrap variant="small">
        {children}
      </Text>
    </TableCell>
  )
}

interface LocalesTableTemplateRowProps {
  name: ReactNode
  localeCode: ReactNode
  setAsMasterButton: ReactNode
  deleteButton: ReactNode
}

function LocalesTableTemplateRow(props: LocalesTableTemplateRowProps) {
  const { name, localeCode, setAsMasterButton, deleteButton } = props
  return (
    <TableRow>
      <TableCell>{name}</TableCell>
      <TableCell>{localeCode}</TableCell>
      <TableCell align="end">
        <ButtonGroup color="grey">
          {setAsMasterButton}
          {deleteButton}
        </ButtonGroup>
      </TableCell>
    </TableRow>
  )
}

const LocalesTableTemplateSetAsMasterButton = forwardRef<
  ElementRef<typeof Button>,
  ButtonProps
>(function LocalesTableTemplateSetAsMasterButton(props, ref) {
  return (
    <Button {...props} ref={ref}>
      Set as master
    </Button>
  )
})

function LocalesTableTemplateDeleteButton(props: ButtonProps) {
  return <Button {...props} startIcon="delete" />
}

export function LocalesTableSkeleton() {
  return (
    <LocalesTableTemplate isLoading={false}>
      <LocalesTableTemplateRow
        name={<Skeleton width={210} height={24} />}
        localeCode={<Skeleton width={34} height={16} />}
        setAsMasterButton={<LocalesTableTemplateSetAsMasterButton disabled />}
        deleteButton={<LocalesTableTemplateDeleteButton disabled />}
      />
    </LocalesTableTemplate>
  )
}
